import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import AutoFixNormalIcon from '@mui/icons-material/AutoFixNormal';
import BrushIcon from '@mui/icons-material/Brush';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import HighlightIcon from '@mui/icons-material/Highlight';
import NearMeIcon from '@mui/icons-material/NearMe';
import RotateLeftIcon from '@mui/icons-material/RotateLeft';
import RotateRightIcon from '@mui/icons-material/RotateRight';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import UndoIcon from '@mui/icons-material/Undo';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { shouldIncludeCredentialsInRequest } from '../../../commonUtils/images';
import '../../../commonUtils/promiseWithResolversPolyfill';
import {
  FeatureGateName,
  useFeatureGate,
} from '../../../commonUtils/featureSchoolGate';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAndroidAppRefreshGate } from '../../../commonUtils/useAndroidAppRefreshGate';
import { Document, Page, pdfjs } from 'react-pdf';
import { File } from 'react-pdf/dist/cjs/shared/types';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import PdfAnnotationLayer, { AnnotationTool } from './PdfAnnotationLayer';
import {
  ANNOTATION_COLORS,
  ANNOTATION_FONT_SIZE,
  ANNOTATION_STROKE_WIDTH,
  Annotation,
  PageAnnotations,
  getDocumentAnnotations,
  saveDocumentAnnotations,
} from './annotationStorage';

interface IProps {
  file: File;
  layoutType: 'single' | 'multiple';
}

declare global {
  interface Window {
    Android: any;
  }
}

const CustomPDFRenderer = (props: IProps) => {
  const { file, layoutType } = props;

  const [pageNumber, setPageNumber] = useState(1);
  const [numPages, setNumPages] = useState<number | null>(null);
  const [scale, setScale] = useState(1.0);
  const [rotation, setRotation] = useState(0);
  const [pageWidths, setPageWidths] = useState<Record<number, number>>({});
  const minPageWidth =
    Object.values(pageWidths).length > 0
      ? Math.min(...Object.values(pageWidths))
      : 0;

  // rendered dimensions per page, used to size the annotation overlay
  const [pageDims, setPageDims] = useState<
    Record<number, { width: number; height: number }>
  >({});

  // annotations are keyed by the document url (only string files are supported)
  const annotationKey = typeof file === 'string' ? file : null;
  const annotationsEnabled = useFeatureGate(FeatureGateName.annotationsEnabled);
  const [annotations, setAnnotations] = useState<PageAnnotations>({});
  const [annotationMode, setAnnotationMode] = useState(false);
  const [tool, setTool] = useState<AnnotationTool>('pen');
  const [color, setColor] = useState(ANNOTATION_COLORS[0]);
  const lastEditedPageRef = useRef<number | null>(null);

  pdfjs.GlobalWorkerOptions.workerSrc = `/pdf.worker.js`;

  const pdfOptions = useMemo(() => {
    if (typeof file === 'string' && shouldIncludeCredentialsInRequest(file)) {
      return { withCredentials: true };
    }
    return undefined;
  }, [file]);

  const handlePageChange = (offset: number) => {
    setPageNumber((prevPage) =>
      Math.min(Math.max(prevPage + offset, 1), numPages || 1)
    );
  };

  const handleZoom = (zoomChange: number) => {
    setScale((prevScale) => Math.max(0.5, prevScale + zoomChange));
  };

  const handleRotate = (angle: number) => {
    setRotation((prevRotation) => (prevRotation + angle) % 360);
  };

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
  };

  // load previously saved annotations whenever the document url changes
  useEffect(() => {
    if (annotationKey) {
      setAnnotations(getDocumentAnnotations(annotationKey));
    } else {
      setAnnotations({});
    }
  }, [annotationKey]);

  const handleAnnotationsChange = useCallback(
    (page: number, pageAnnotations: Annotation[]) => {
      lastEditedPageRef.current = page;
      setAnnotations((prev) => {
        const next = { ...prev, [page]: pageAnnotations };
        if (annotationKey) {
          saveDocumentAnnotations(annotationKey, next);
        }
        return next;
      });
    },
    [annotationKey]
  );

  const handleUndo = useCallback(() => {
    const page = lastEditedPageRef.current;
    if (page == null) return;
    setAnnotations((prev) => {
      const strokes = prev[page] || [];
      if (strokes.length === 0) return prev;
      const next = { ...prev, [page]: strokes.slice(0, -1) };
      if (annotationKey) {
        saveDocumentAnnotations(annotationKey, next);
      }
      return next;
    });
  }, [annotationKey]);

  const handleClearAll = useCallback(() => {
    setAnnotations({});
    lastEditedPageRef.current = null;
    if (annotationKey) {
      saveDocumentAnnotations(annotationKey, {});
    }
  }, [annotationKey]);

  const handleRenderSuccess = useCallback(
    (
      page: number,
      dims: { width: number; height: number }
    ) => {
      setPageDims((prev) => ({ ...prev, [page]: dims }));
    },
    []
  );

  const containerRef = useRef<HTMLDivElement>(null);

  const updatePageWidth = useCallback(
    (pageNumber: number, originalWidth: number, originalHeight: number) => {
      const containerW = containerRef.current?.parentElement?.offsetWidth || 0;
      const maxWidth = originalHeight > originalWidth ? 660 : 900;
      setPageWidths((prev) => ({
        ...prev,
        [pageNumber]: Math.min(containerW, maxWidth) - 10,
      }));
    },
    []
  );

  useAndroidAppRefreshGate();

  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current?.parentElement) {
        Object.keys(pageWidths).forEach((page) => {
          const width = pageWidths[+page];
          const containerW =
            containerRef.current?.parentElement?.offsetWidth || 0;
          setPageWidths((prev) => ({
            ...prev,
            [+page]: Math.min(containerW, width),
          }));
        });
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [pageWidths]);

  if (!file) return null;

  return (
    <Box
      id="my-pdf-renderer"
      display="flex"
      flexDirection="column"
      alignItems="stretch"
      ref={containerRef}
      sx={{
        background: '#ddd',
        '& .react-pdf__Page': {
          marginTop: '10px',
          marginBottom: '10px',
          border: '1px solid #ccbaba',
        },
      }}
      onClick={(ev) => ev.stopPropagation()}
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-evenly"
        py="10px"
        width="100%"
        zIndex={1000}
        sx={{
          position: 'sticky',
          top: 0,
          backgroundColor: '#ddd',
        }}
      >
        <IconButton onClick={() => handleZoom(0.2)}>
          <ZoomInIcon />
        </IconButton>
        <IconButton onClick={() => handleZoom(-0.2)}>
          <ZoomOutIcon />
        </IconButton>
        <IconButton onClick={() => handleRotate(-90)}>
          <RotateLeftIcon />
        </IconButton>
        <IconButton onClick={() => handleRotate(90)}>
          <RotateRightIcon />
        </IconButton>

        {annotationsEnabled && annotationKey && (
          <>
            <Box
              sx={{
                width: '1px',
                height: '24px',
                backgroundColor: '#bbb',
                mx: '4px',
              }}
            />
            <Tooltip title="Annotate">
              <IconButton
                onClick={() => setAnnotationMode((prev) => !prev)}
                sx={{ color: annotationMode ? 'primary.main' : undefined }}
              >
                <EditIcon />
              </IconButton>
            </Tooltip>
            {annotationMode && (
              <>
                <Tooltip title="Pen">
                  <IconButton
                    onClick={() => setTool('pen')}
                    sx={{ color: tool === 'pen' ? 'primary.main' : undefined }}
                  >
                    <BrushIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Highlight">
                  <IconButton
                    onClick={() => setTool('highlight')}
                    sx={{
                      color: tool === 'highlight' ? 'primary.main' : undefined,
                    }}
                  >
                    <HighlightIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Text">
                  <IconButton
                    onClick={() => setTool('text')}
                    sx={{ color: tool === 'text' ? 'primary.main' : undefined }}
                  >
                    <TextFieldsIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Select / edit">
                  <IconButton
                    onClick={() => setTool('select')}
                    sx={{
                      color: tool === 'select' ? 'primary.main' : undefined,
                    }}
                  >
                    <NearMeIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Eraser">
                  <IconButton
                    onClick={() => setTool('eraser')}
                    sx={{
                      color: tool === 'eraser' ? 'primary.main' : undefined,
                    }}
                  >
                    <AutoFixNormalIcon />
                  </IconButton>
                </Tooltip>
                <Box display="flex" alignItems="center" gap="6px" mx="4px">
                  {ANNOTATION_COLORS.map((c) => (
                    <Box
                      key={c}
                      onClick={() => {
                        setColor(c);
                        if (tool === 'select' || tool === 'eraser') {
                          setTool('pen');
                        }
                      }}
                      sx={{
                        width: '20px',
                        height: '20px',
                        borderRadius: '50%',
                        backgroundColor: c,
                        cursor: 'pointer',
                        boxShadow: '0 0 0 1px #ccc',
                        border:
                          color === c
                            ? '2px solid #333'
                            : '2px solid #fff',
                      }}
                    />
                  ))}
                </Box>
                <Tooltip title="Undo">
                  <IconButton onClick={handleUndo}>
                    <UndoIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Clear all">
                  <IconButton onClick={handleClearAll}>
                    <DeleteOutlineIcon />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </>
        )}
      </Box>
      {layoutType === 'single' && (
        <>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            gap="10px"
            width="100%"
            py="10px"
            zIndex={1000}
          >
            <IconButton
              onClick={() => handlePageChange(-1)}
              disabled={pageNumber <= 1}
            >
              <ArrowBackIosNewIcon fontSize="small" />
            </IconButton>
            <Typography variant="body1">
              Page {pageNumber} of {numPages}
            </Typography>
            <IconButton
              onClick={() => handlePageChange(1)}
              disabled={pageNumber >= (numPages || 1)}
            >
              <ArrowForwardIosIcon fontSize="small" />
            </IconButton>
          </Box>
          <Box
            sx={{
              width: '100%',
              overflowX: 'auto',
              overflowY: 'hidden',
              overscrollBehaviorX: 'contain',
              display: 'flex',
              justifyContent: 'flex-start',
              '&>div': {
                mx: 'auto',
              },
            }}
          >
            <Document
              file={file}
              onLoadSuccess={onDocumentLoadSuccess}
              onLoadError={(error) =>
                console.error('Error loading document:', error)
              }
              options={pdfOptions}
            >
              <Box sx={{ position: 'relative', width: 'fit-content', mx: 'auto' }}>
                <Page
                  pageNumber={pageNumber}
                  scale={scale}
                  rotate={rotation}
                  width={minPageWidth * scale || 660 * scale}
                  onRenderSuccess={({ width, height }) => {
                    updatePageWidth(pageNumber, width, height);
                    handleRenderSuccess(pageNumber, { width, height });
                  }}
                />
                {annotationsEnabled && annotationKey && pageDims[pageNumber] && (
                  <PdfAnnotationLayer
                    width={pageDims[pageNumber].width}
                    height={pageDims[pageNumber].height}
                    annotations={annotations[pageNumber] || []}
                    tool={annotationMode ? tool : null}
                    color={color}
                    strokeWidth={ANNOTATION_STROKE_WIDTH}
                    fontSize={ANNOTATION_FONT_SIZE}
                    onChange={(next) =>
                      handleAnnotationsChange(pageNumber, next)
                    }
                  />
                )}
              </Box>
            </Document>
          </Box>
        </>
      )}

      {layoutType === 'multiple' && (
        <Box
          sx={{
            width: '100%',
            overflowX: 'auto',
            overflowY: 'hidden',
            overscrollBehaviorX: 'contain',
            display: 'flex',
            justifyContent: 'flex-start',
            '&>div': {
              mx: 'auto',
            },
          }}
        >
          <Document
            file={file}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={(error) =>
              console.error('Error loading document:', error)
            }
            options={pdfOptions}
          >
            {Array.from({ length: numPages || 0 }, (_, index) => {
              const page = index + 1;
              return (
                <Box
                  key={`page_${page}`}
                  sx={{ position: 'relative', width: 'fit-content', mx: 'auto' }}
                >
                  <Page
                    pageNumber={page}
                    scale={scale}
                    rotate={rotation}
                    width={minPageWidth * scale || 650}
                    onRenderSuccess={({
                      width,
                      height,
                      originalWidth,
                      originalHeight,
                    }) => {
                      updatePageWidth(page, originalWidth, originalHeight);
                      handleRenderSuccess(page, { width, height });
                    }}
                  />
                  {annotationsEnabled && annotationKey && pageDims[page] && (
                    <PdfAnnotationLayer
                      width={pageDims[page].width}
                      height={pageDims[page].height}
                      annotations={annotations[page] || []}
                      tool={annotationMode ? tool : null}
                      color={color}
                      strokeWidth={ANNOTATION_STROKE_WIDTH}
                      fontSize={ANNOTATION_FONT_SIZE}
                      onChange={(next) => handleAnnotationsChange(page, next)}
                    />
                  )}
                </Box>
              );
            })}
          </Document>
        </Box>
      )}
    </Box>
  );
};

CustomPDFRenderer.fileTypes = ['pdf', 'application/pdf'];
CustomPDFRenderer.weight = 1;

export default CustomPDFRenderer;
